import {
  arrow,
  autoUpdate,
  computePosition,
  flip,
  offset,
  shift,
} from '@floating-ui/dom';

// Usage contract: put plain text in data-smaac-tooltip and optionally set
// data-smaac-tooltip-placement. A direct child template marked with
// data-smaac-tooltip-content provides optional structured content. Existing
// title attributes are adapted for the duration of an interaction.
const TOOLTIP_ID = 'smaac-global-tooltip';
const EXPLICIT_TRIGGER_SELECTOR = '[data-smaac-tooltip], [data-tooltip], [title]';
const ACTION_TRIGGER_SELECTOR = 'button, a, [role="button"]';
const TRIGGER_SELECTOR = [
  EXPLICIT_TRIGGER_SELECTOR,
  ACTION_TRIGGER_SELECTOR,
].join(', ');
const HELP_TRIGGER_SELECTOR = '.smaac-tooltip-trigger';
const HOVER_SHOW_DELAY_MS = 180;
const HOVER_HIDE_DELAY_MS = 100;
const POPOVER_SUPPORTED = (
  typeof HTMLElement !== 'undefined' &&
  typeof HTMLElement.prototype.showPopover === 'function'
);
const VALID_PLACEMENTS = new Set([
  'top',
  'top-start',
  'top-end',
  'right',
  'right-start',
  'right-end',
  'bottom',
  'bottom-start',
  'bottom-end',
  'left',
  'left-start',
  'left-end',
]);

let initialized = false;
let tooltip;
let tooltipContent;
let tooltipArrow;
let tooltipUsesPopover = false;
let activeTrigger;
let pendingTrigger;
let positionCleanup;
let showTimer;
let hideTimer;
let transitionTimer;
let showSequence = 0;
let tooltipStateObserver;
let reachabilityEnhancementTimer;
let reachabilityEnhancementTimerType;

const pendingReachabilityRoots = new Set();

const titleStates = new WeakMap();
const autoLabelValues = new WeakMap();

export function normalizeTooltipPlacement(value) {
  return VALID_PLACEMENTS.has(value) ? value : 'top';
}

export function getTooltipArrowStyles(placement, arrowData = {}) {
  const side = placement.split('-')[0];
  const staticSide = {
    top: 'bottom',
    right: 'left',
    bottom: 'top',
    left: 'right',
  }[side];

  return {
    left: arrowData.x == null ? '' : `${arrowData.x}px`,
    top: arrowData.y == null ? '' : `${arrowData.y}px`,
    right: '',
    bottom: '',
    [staticSide]: '-4px',
  };
}

export function hasTooltipStyles(computedStyle) {
  return computedStyle
    ?.getPropertyValue('--smaac-tooltip-ready')
    .trim() === '1';
}

function tooltipStylesReady() {
  return Boolean(tooltip && hasTooltipStyles(window.getComputedStyle(tooltip)));
}

function clearTimer(timer) {
  if (timer) window.clearTimeout(timer);
}

function clearReachabilityEnhancementTimer() {
  if (!reachabilityEnhancementTimer) return;
  if (reachabilityEnhancementTimerType === 'idle') {
    window.cancelIdleCallback(reachabilityEnhancementTimer);
  } else {
    window.clearTimeout(reachabilityEnhancementTimer);
  }
  reachabilityEnhancementTimer = null;
  reachabilityEnhancementTimerType = null;
}

function isInteractive(element) {
  return element.matches('button, a, input, select, textarea, [role="button"], [tabindex]');
}

function hasAssociatedLabel(element) {
  return 'labels' in element && element.labels && element.labels.length > 0;
}

function visibleAccessibleText(element) {
  if (element.matches('input[type="button"], input[type="submit"], input[type="reset"]')) {
    return (element.value || '').trim();
  }
  if (element.matches('input, select, textarea')) return '';
  return (element.textContent || '').trim();
}

function normalizeText(value) {
  return (value || '').replace(/\s+/g, ' ').trim();
}

function accessibleNameText(element) {
  const labelledBy = (element.getAttribute('aria-labelledby') || '')
    .split(/\s+/)
    .filter(Boolean)
    .map((id) => element.ownerDocument.getElementById(id)?.textContent || '')
    .join(' ');
  if (normalizeText(labelledBy)) return normalizeText(labelledBy);

  const ariaLabel = normalizeText(element.getAttribute('aria-label'));
  if (ariaLabel) return ariaLabel;

  if (hasAssociatedLabel(element)) {
    const labelText = Array.from(element.labels)
      .map((label) => label.textContent || '')
      .join(' ');
    if (normalizeText(labelText)) return normalizeText(labelText);
  }
  return normalizeText(visibleAccessibleText(element));
}

function explicitTooltipContent(element) {
  return (
    (element.getAttribute('data-smaac-tooltip') || '').trim() ||
    (element.getAttribute('data-tooltip') || '').trim()
  );
}

function automaticActionTooltipContent(element) {
  if (!element.matches(ACTION_TRIGGER_SELECTOR)) return '';
  const srOnlyLabel = Array.from(element.querySelectorAll('.sr-only'))
    .map((node) => (node.textContent || '').trim())
    .filter(Boolean)
    .join(' ');
  const accessibleLabel = (
    (element.getAttribute('aria-label') || '').trim() ||
    srOnlyLabel
  );
  if (!accessibleLabel) return '';
  const clone = element.cloneNode(true);
  clone.querySelectorAll('svg, [hidden], .hidden, .sr-only').forEach((node) => node.remove());
  const visibleText = (clone.textContent || '').trim();
  if (/\p{L}|\p{N}/u.test(visibleText)) return '';
  return accessibleLabel;
}

function currentTooltipContent(element) {
  return (
    explicitTooltipContent(element) ||
    (element.getAttribute('title') || '').trim() ||
    automaticActionTooltipContent(element)
  );
}

function hasAccessibleName(element) {
  return Boolean(accessibleNameText(element));
}

function clearAutoAccessibleName(element) {
  const ownedValue = autoLabelValues.get(element);
  if (ownedValue && element.getAttribute('aria-label') === ownedValue) {
    element.removeAttribute('aria-label');
  }
  autoLabelValues.delete(element);
  delete element.dataset.smaacTooltipAutoLabel;
}

function syncGeneratedAccessibleName(element, content) {
  const ownedValue = autoLabelValues.get(element);
  if (ownedValue && element.getAttribute('aria-label') !== ownedValue) {
    autoLabelValues.delete(element);
    delete element.dataset.smaacTooltipAutoLabel;
  } else if (ownedValue && !content) {
    clearAutoAccessibleName(element);
  } else if (ownedValue && ownedValue !== content) {
    element.setAttribute('aria-label', content);
    autoLabelValues.set(element, content);
  }

  if (
    content &&
    isInteractive(element) &&
    !hasAccessibleName(element)
  ) {
    element.setAttribute('aria-label', content);
    element.dataset.smaacTooltipAutoLabel = 'true';
    autoLabelValues.set(element, content);
  }
}

function setTitleFromController(element, value) {
  if (element.getAttribute('title') === value) return;
  element.setAttribute('title', value);
}

function wrapsDisabledControl(element) {
  if (element.matches(':disabled') || element.getAttribute('aria-disabled') === 'true') return true;
  return Boolean(element.querySelector?.(':scope > button:disabled, :scope > input:disabled, :scope > select:disabled, :scope > textarea:disabled'));
}

function syncTooltipReachability(element) {
  const content = (
    (element.getAttribute('title') || '').trim() ||
    explicitTooltipContent(element)
  );
  const hasContent = Boolean(content);
  const isDisabledWrapper = hasContent && wrapsDisabledControl(element);
  syncGeneratedAccessibleName(element, content);
  const shouldAutoDescribe = Boolean(
    hasContent &&
    normalizeText(content) !== accessibleNameText(element) &&
    (!isInteractive(element) || hasAccessibleName(element)) &&
    element.dataset.smaacTooltipAutoLabel !== 'true' &&
    (
      !element.hasAttribute('aria-description') ||
      element.dataset.smaacTooltipAutoDescription === 'true'
    )
  );

  if (shouldAutoDescribe) {
    element.setAttribute('aria-description', content);
    element.dataset.smaacTooltipAutoDescription = 'true';
  } else if (element.dataset.smaacTooltipAutoDescription === 'true') {
    element.removeAttribute('aria-description');
    delete element.dataset.smaacTooltipAutoDescription;
  }

  if (isDisabledWrapper && !isInteractive(element)) {
    element.tabIndex = 0;
    element.dataset.smaacTooltipAutoTabindex = 'true';
  }
  if (isDisabledWrapper) element.dataset.smaacTooltipAutoToggle = 'true';

  if (!isDisabledWrapper && element.dataset.smaacTooltipAutoTabindex === 'true') {
    element.removeAttribute('tabindex');
    delete element.dataset.smaacTooltipAutoTabindex;
  }
  if (!isDisabledWrapper) delete element.dataset.smaacTooltipAutoToggle;
}

function prepareTooltipContent(element) {
  const existingState = titleStates.get(element);
  if (existingState) {
    return explicitTooltipContent(element) || automaticActionTooltipContent(element);
  }

  const explicitContent = explicitTooltipContent(element);
  const nativeTitle = element.getAttribute('title');
  if (nativeTitle == null || !nativeTitle.trim()) {
    syncTooltipReachability(element);
    return explicitContent || automaticActionTooltipContent(element);
  }

  const state = {
    source: explicitContent ? 'explicit' : 'title',
    title: nativeTitle.trim(),
    titleIsCurrent: true,
  };
  titleStates.set(element, state);

  if (!explicitContent) {
    element.setAttribute('data-smaac-tooltip', state.title);
    element.dataset.smaacTooltipFromTitle = 'true';
  }
  syncGeneratedAccessibleName(element, state.title);
  setTitleFromController(element, '');
  syncTooltipReachability(element);

  return explicitContent || state.title;
}

function cleanupTitleAdapter(element) {
  if (!element) return;
  const state = titleStates.get(element);
  if (!state) return;

  if (state.titleIsCurrent && element.getAttribute('title') === '') {
    setTitleFromController(element, state.title);
  }
  if (state.source === 'title' && element.dataset.smaacTooltipFromTitle === 'true') {
    if (element.getAttribute('data-smaac-tooltip') === state.title) {
      element.removeAttribute('data-smaac-tooltip');
    }
    delete element.dataset.smaacTooltipFromTitle;
  }
  titleStates.delete(element);
  syncTooltipReachability(element);
}

function refreshTitleState(element, record) {
  const state = titleStates.get(element);
  if (!state) {
    syncTooltipReachability(element);
    if (
      (activeTrigger === element || pendingTrigger === element) &&
      (element.getAttribute('title') || '').trim()
    ) {
      prepareTooltipContent(element);
      refreshObservedTrigger(element);
    }
    return;
  }

  if (
    element.getAttribute('title') === '' &&
    (record.oldValue || '').trim() === state.title
  ) return;

  const nextTitle = (element.getAttribute('title') || '').trim();
  if (!nextTitle) {
    state.titleIsCurrent = false;
    if (state.source === 'title') {
      element.removeAttribute('data-smaac-tooltip');
      delete element.dataset.smaacTooltipFromTitle;
      if (activeTrigger === element || pendingTrigger === element) {
        hideTooltip({ immediate: true });
      }
    }
    syncTooltipReachability(element);
    return;
  }

  state.title = nextTitle;
  state.titleIsCurrent = true;
  if (state.source === 'title') {
    element.setAttribute('data-smaac-tooltip', nextTitle);
    element.dataset.smaacTooltipFromTitle = 'true';
    if (activeTrigger === element) {
      renderTooltipContent(element, nextTitle);
      updatePosition(element);
    }
  }
  syncGeneratedAccessibleName(element, nextTitle);
  setTitleFromController(element, '');
  syncTooltipReachability(element);
}

function refreshObservedTrigger(trigger) {
  syncTooltipReachability(trigger);
  if (activeTrigger !== trigger && pendingTrigger !== trigger) return true;

  const content = currentTooltipContent(trigger);
  if (!content) {
    hideTooltip({ immediate: true });
    return false;
  }
  if (activeTrigger === trigger) {
    renderTooltipContent(trigger, content);
    updatePosition(trigger);
  }
  return true;
}

function observeTooltipState() {
  if (tooltipStateObserver || !document.documentElement) return;
  tooltipStateObserver = new MutationObserver((records) => {
    const finalTitleRecords = new Map();
    records.forEach((record) => {
      if (record.type === 'childList') {
        record.addedNodes.forEach((node) => {
          if (node instanceof Element) pendingReachabilityRoots.add(node);
        });
        record.removedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          pendingReachabilityRoots.forEach((root) => {
            if (node === root || node.contains(root)) pendingReachabilityRoots.delete(root);
          });
        });
        if (!pendingReachabilityRoots.size) clearReachabilityEnhancementTimer();
        if (
          (activeTrigger && !activeTrigger.isConnected) ||
          (pendingTrigger && !pendingTrigger.isConnected)
        ) {
          hideTooltip({ immediate: true });
        }
        return;
      }
      if (record.attributeName === 'title') {
        finalTitleRecords.set(record.target, record);
        return;
      }
      if (
        record.attributeName === 'data-smaac-tooltip' ||
        record.attributeName === 'data-tooltip'
      ) {
        syncTooltipReachability(record.target);
      }
      const trackedTrigger = (
        record.target === activeTrigger || record.target === pendingTrigger
      ) ? record.target : null;
      if (trackedTrigger && !refreshObservedTrigger(trackedTrigger)) return;

      let trigger = record.target;
      while (trigger instanceof Element) {
        if (trigger !== trackedTrigger && trigger.matches(TRIGGER_SELECTOR)) {
          if (!refreshObservedTrigger(trigger)) return;
          break;
        }
        trigger = trigger.parentElement;
      }
    });
    finalTitleRecords.forEach((record, element) => {
      refreshTitleState(element, record);
    });
    scheduleReachabilityEnhancement();
  });
  tooltipStateObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: [
      'title',
      'disabled',
      'aria-disabled',
      'data-smaac-tooltip',
      'data-tooltip',
      'aria-label',
    ],
    attributeOldValue: true,
    childList: true,
    subtree: true,
  });
}

function scheduleReachabilityEnhancement() {
  if (!pendingReachabilityRoots.size || reachabilityEnhancementTimer) return;

  const enhancePendingRoots = () => {
    reachabilityEnhancementTimer = null;
    reachabilityEnhancementTimerType = null;
    const roots = Array.from(pendingReachabilityRoots);
    pendingReachabilityRoots.clear();
    roots.filter((root) => root.isConnected).forEach(enhanceTooltipReachability);
  };

  if (typeof window.requestIdleCallback === 'function') {
    reachabilityEnhancementTimer = window.requestIdleCallback(enhancePendingRoots, { timeout: 200 });
    reachabilityEnhancementTimerType = 'idle';
  } else {
    reachabilityEnhancementTimer = window.setTimeout(enhancePendingRoots, 0);
    reachabilityEnhancementTimerType = 'timeout';
  }
}

function enhanceTooltipReachability(root = document) {
  if (root instanceof Element && root.matches(EXPLICIT_TRIGGER_SELECTOR)) {
    syncTooltipReachability(root);
  }
  root.querySelectorAll?.(EXPLICIT_TRIGGER_SELECTOR).forEach(syncTooltipReachability);
}

function bindTooltipEvents(element) {
  element.addEventListener('pointerenter', () => {
    clearTimer(hideTimer);
    hideTimer = null;
  });
  element.addEventListener('pointerleave', () => scheduleHide(0));
}

function createTooltip() {
  const existing = document.getElementById(TOOLTIP_ID);
  if (existing) existing.remove();

  tooltip = document.createElement('div');
  tooltip.id = TOOLTIP_ID;
  tooltip.className = 'smaac-tooltip';
  tooltip.setAttribute('role', 'tooltip');
  tooltipUsesPopover = POPOVER_SUPPORTED;
  if (tooltipUsesPopover) tooltip.setAttribute('popover', 'manual');
  tooltip.hidden = true;
  tooltip.dataset.visible = 'false';

  tooltipContent = document.createElement('div');
  tooltipContent.className = 'smaac-tooltip__content';
  tooltip.appendChild(tooltipContent);

  tooltipArrow = document.createElement('div');
  tooltipArrow.className = 'smaac-tooltip__arrow';
  tooltip.appendChild(tooltipArrow);

  bindTooltipEvents(tooltip);
  document.body.appendChild(tooltip);
}

function ensureTooltip() {
  if (!tooltip?.isConnected) createTooltip();
}

function findTrigger(target) {
  if (!(target instanceof Element)) return null;
  if (!tooltipStylesReady()) return null;
  let trigger = target.closest(TRIGGER_SELECTOR);
  while (trigger) {
    if (
      wrapsDisabledControl(trigger) &&
      !explicitTooltipContent(trigger) &&
      !trigger.hasAttribute('title')
    ) {
      const disabledWrapper = trigger.parentElement?.closest(EXPLICIT_TRIGGER_SELECTOR);
      if (
        disabledWrapper &&
        wrapsDisabledControl(disabledWrapper) &&
        prepareTooltipContent(disabledWrapper)
      ) return disabledWrapper;
    }
    if (
      trigger !== tooltip &&
      !tooltip?.contains(trigger) &&
      !trigger.matches('iframe, option') &&
      prepareTooltipContent(trigger)
    ) return trigger;
    trigger = trigger.parentElement?.closest(TRIGGER_SELECTOR);
  }
  return null;
}

function updateDescribedBy(trigger, add) {
  const ids = new Set((trigger.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));
  const description = (
    explicitTooltipContent(trigger) ||
    (trigger.getAttribute('title') || '').trim()
  );
  const hasDistinctDescription = Boolean(
    description &&
    normalizeText(description) !== accessibleNameText(trigger)
  );
  if (
    add &&
    hasDistinctDescription &&
    trigger.dataset.smaacTooltipAutoLabel !== 'true'
  ) ids.add(TOOLTIP_ID);
  else ids.delete(TOOLTIP_ID);

  if (ids.size) trigger.setAttribute('aria-describedby', Array.from(ids).join(' '));
  else trigger.removeAttribute('aria-describedby');
}

function mountTooltipFor(trigger) {
  const host = trigger.closest('dialog[open]') || document.body;
  if (tooltip.parentElement !== host) host.appendChild(tooltip);
}

function openTooltipLayer() {
  tooltip.hidden = false;
  if (!tooltipUsesPopover || tooltip.matches(':popover-open')) return;
  try {
    tooltip.showPopover();
  } catch {
    tooltip.removeAttribute('popover');
    tooltipUsesPopover = false;
  }
}

function closeTooltipLayer() {
  if (tooltipUsesPopover && tooltip.matches(':popover-open')) tooltip.hidePopover();
  tooltip.hidden = true;
  tooltipContent.textContent = '';
}

function renderTooltipContent(trigger, content) {
  const contentTemplate = trigger.querySelector(
    ':scope > template[data-smaac-tooltip-content]'
  );
  tooltip.classList.toggle('smaac-tooltip--structured', Boolean(contentTemplate));
  tooltipContent.replaceChildren(
    contentTemplate ? contentTemplate.content.cloneNode(true) : document.createTextNode(content)
  );
}

async function updatePosition(trigger) {
  if (!tooltip) return false;
  if (!trigger?.isConnected) {
    if (activeTrigger === trigger || pendingTrigger === trigger) {
      hideTooltip({ immediate: true });
    }
    return false;
  }

  const requestedPlacement = normalizeTooltipPlacement(
    trigger.getAttribute('data-smaac-tooltip-placement')
  );
  let result;
  try {
    result = await computePosition(trigger, tooltip, {
      placement: requestedPlacement,
      strategy: 'fixed',
      middleware: [
        offset(8),
        flip({ padding: 8 }),
        shift({ padding: 8 }),
        arrow({ element: tooltipArrow, padding: 6 }),
      ],
    });
  } catch {
    if (activeTrigger === trigger || pendingTrigger === trigger) {
      hideTooltip({ immediate: true });
    }
    return false;
  }

  if (activeTrigger !== trigger) return false;
  tooltip.style.setProperty('--smaac-tooltip-x', `${result.x}px`);
  tooltip.style.setProperty('--smaac-tooltip-y', `${result.y}px`);
  tooltip.dataset.placement = result.placement;
  Object.entries(getTooltipArrowStyles(result.placement, result.middlewareData.arrow))
    .forEach(([property, value]) => {
      tooltipArrow.style.setProperty(`--smaac-tooltip-arrow-${property}`, value);
    });
  return true;
}

function hideTooltip({ immediate = false } = {}) {
  clearTimer(showTimer);
  clearTimer(hideTimer);
  clearTimer(transitionTimer);
  showTimer = null;
  hideTimer = null;
  transitionTimer = null;
  showSequence += 1;

  const triggersToClean = new Set([activeTrigger, pendingTrigger].filter(Boolean));
  if (activeTrigger) updateDescribedBy(activeTrigger, false);
  activeTrigger = null;
  pendingTrigger = null;
  positionCleanup?.();
  positionCleanup = null;
  triggersToClean.forEach(cleanupTitleAdapter);

  if (!tooltip) return;
  tooltip.dataset.visible = 'false';

  const finish = () => {
    if (!activeTrigger) closeTooltipLayer();
  };
  if (immediate) finish();
  else transitionTimer = window.setTimeout(finish, 120);
}

function handleTooltipEscape(event) {
  if (event.key !== 'Escape') return;
  if (!activeTrigger) {
    if (pendingTrigger) hideTooltip({ immediate: true });
    return;
  }
  if (isToggleTrigger(activeTrigger)) {
    event.preventDefault();
    event.stopImmediatePropagation();
  }
  hideTooltip({ immediate: true });
}

function showTooltip(trigger) {
  ensureTooltip();
  if (!tooltipStylesReady()) {
    if (pendingTrigger === trigger) pendingTrigger = null;
    showTimer = null;
    cleanupTitleAdapter(trigger);
    return;
  }
  if (!trigger?.isConnected) {
    if (pendingTrigger === trigger) pendingTrigger = null;
    showTimer = null;
    cleanupTitleAdapter(trigger);
    return;
  }
  const content = prepareTooltipContent(trigger);
  if (!content) {
    if (pendingTrigger === trigger) pendingTrigger = null;
    showTimer = null;
    cleanupTitleAdapter(trigger);
    return;
  }

  clearTimer(showTimer);
  clearTimer(hideTimer);
  clearTimer(transitionTimer);
  showTimer = null;
  hideTimer = null;
  transitionTimer = null;
  if (pendingTrigger === trigger) pendingTrigger = null;

  if (activeTrigger && activeTrigger !== trigger) hideTooltip({ immediate: true });

  mountTooltipFor(trigger);
  activeTrigger = trigger;
  const sequence = ++showSequence;
  renderTooltipContent(trigger, content);
  tooltip.dataset.visible = 'false';
  tooltip.style.setProperty('--smaac-tooltip-x', '-9999px');
  tooltip.style.setProperty('--smaac-tooltip-y', '-9999px');
  openTooltipLayer();
  updateDescribedBy(trigger, true);

  positionCleanup?.();
  positionCleanup = autoUpdate(trigger, tooltip, () => updatePosition(trigger));
  updatePosition(trigger).then((positioned) => {
    if (!positioned || activeTrigger !== trigger || sequence !== showSequence) return;
    tooltip.dataset.visible = 'true';
  });
}

function scheduleShow(trigger, delay = HOVER_SHOW_DELAY_MS) {
  clearTimer(showTimer);
  clearTimer(hideTimer);
  hideTimer = null;
  if (activeTrigger === trigger) return;
  if (pendingTrigger && pendingTrigger !== trigger) cleanupTitleAdapter(pendingTrigger);
  pendingTrigger = trigger;
  showTimer = window.setTimeout(() => showTooltip(trigger), delay);
}

function scheduleHide(delay = HOVER_HIDE_DELAY_MS) {
  clearTimer(showTimer);
  clearTimer(hideTimer);
  showTimer = null;
  hideTimer = window.setTimeout(() => hideTooltip(), delay);
}

function isInsideTooltip(target) {
  return target instanceof Node && tooltip?.contains(target);
}

function isToggleTrigger(trigger) {
  return (
    trigger.matches(HELP_TRIGGER_SELECTOR) ||
    trigger.dataset.smaacTooltipToggle === 'true' ||
    trigger.dataset.smaacTooltipAutoToggle === 'true'
  );
}

function triggerWasCleaned(root, trigger) {
  return Boolean(
    root && trigger &&
    (root === trigger || root.contains?.(trigger))
  );
}

function bindEvents() {
  document.addEventListener('keydown', handleTooltipEscape, true);

  document.addEventListener('pointerover', (event) => {
    if (event.pointerType === 'touch') return;
    const trigger = findTrigger(event.target);
    if (!trigger || trigger.contains(event.relatedTarget)) return;
    scheduleShow(trigger);
  }, true);

  document.addEventListener('pointerout', (event) => {
    if (event.pointerType === 'touch') return;
    const trigger = findTrigger(event.target);
    if (!trigger || trigger.contains(event.relatedTarget)) return;
    if (isInsideTooltip(event.relatedTarget)) return;
    if (activeTrigger === trigger || pendingTrigger === trigger) scheduleHide();
  }, true);

  document.addEventListener('focusin', (event) => {
    const trigger = findTrigger(event.target);
    if (trigger) scheduleShow(trigger, 0);
  }, true);

  document.addEventListener('focusout', (event) => {
    const trigger = findTrigger(event.target);
    if (!trigger || trigger.contains(event.relatedTarget) || isInsideTooltip(event.relatedTarget)) return;
    if (activeTrigger === trigger || pendingTrigger === trigger) scheduleHide();
  }, true);

  document.addEventListener('pointerdown', (event) => {
    const trigger = findTrigger(event.target);
    if (!trigger) {
      if (!isInsideTooltip(event.target)) hideTooltip();
      return;
    }

    if (
      !isToggleTrigger(trigger) &&
      (activeTrigger === trigger || pendingTrigger === trigger)
    ) {
      hideTooltip({ immediate: true });
      return;
    }

    if (event.pointerType !== 'mouse' && isToggleTrigger(trigger)) {
      if (activeTrigger === trigger) hideTooltip();
      else showTooltip(trigger);
    }
  }, true);

  document.addEventListener('click', (event) => {
    const trigger = findTrigger(event.target);
    if (!trigger) return;
    if (!isToggleTrigger(trigger)) {
      if (activeTrigger === trigger || pendingTrigger === trigger) {
        hideTooltip({ immediate: true });
      }
      return;
    }
    event.stopPropagation();

    if (event.detail === 0) {
      event.preventDefault();
      if (activeTrigger === trigger) hideTooltip();
      else showTooltip(trigger);
    }
  }, true);

  document.addEventListener('htmx:beforeCleanupElement', (event) => {
    const root = event.detail?.elt || event.target;
    if (
      triggerWasCleaned(root, activeTrigger) ||
      triggerWasCleaned(root, pendingTrigger) ||
      root?.contains?.(tooltip)
    ) {
      hideTooltip({ immediate: true });
    }
  });

  document.addEventListener('close', (event) => {
    if (event.target.matches?.('dialog') && triggerWasCleaned(event.target, activeTrigger)) {
      hideTooltip({ immediate: true });
    }
  }, true);
}

export function initTooltipSystem(root = document) {
  if (!document.body) return;
  if (!initialized) {
    initialized = true;
    createTooltip();
    bindEvents();
    observeTooltipState();
  } else {
    ensureTooltip();
  }
  enhanceTooltipReachability(root);
}
